// ─── KpiHistoryModal.jsx ────────────────────────────────────────────────────
//
// Renders the daily history of one KPI for the current brand. Triggered by
// the small 📊 button on each Dashboard KPI card. Shows a sparkline chart
// (recharts AreaChart) and a table of (date, value, delta) rows.
//
// Data is fetched from /api/kpi-history when the modal opens. The user only
// cares about end-of-day values, so each row represents one day's final
// snapshot. If there's no history yet (cron hasn't run), shows an empty
// state pointing to "First snapshot at midnight UTC tonight".

import { useState, useEffect, useMemo } from "react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";
import { BottomSheet } from "./ui.jsx";
import { useTheme } from "../lib/ThemeContext.jsx";
import { FONT, RADIUS } from "../lib/theme.js";
import { currSym } from "../lib/calculations.js";
import { supabase } from "../hooks/useData.js";

// Format helpers — KPIs have different units (currency, days, units, etc.)
function formatValue(kpiId, value) {
  if (value == null) return "—";
  switch (kpiId) {
    case "moneyInInventory":
    case "blendedPayout":
    case "todayBalance":
    case "reserveHeld":
      return currSym() + Math.round(value).toLocaleString();
    case "stockoutDays":
    case "runwayDays":
    case "cashToCash":
      return value < 0 ? "—" : value >= 9999 ? "∞" : `${Math.round(value)}d`;
    case "totalUnitsInStock":
      return Math.round(value).toLocaleString() + " units";
    default:
      return String(Math.round(value * 100) / 100);
  }
}

const KPI_LABELS = {
  moneyInInventory: "Money in Inventory",
  stockoutDays: "Stockout Date",
  runwayDays: "Runway",
  blendedPayout: "Blended Payout/Unit",
  todayBalance: "Available Today",
  reserveHeld: "Reserve Held",
  cashToCash: "Cash-to-Cash",
  totalUnitsInStock: "Total Units in Stock",
  brandShare: "Brand Share",
};

export default function KpiHistoryModal({ open, onClose, kpiId, dataKey = "main" }) {
  const T = useTheme();
  const [snapshots, setSnapshots] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !kpiId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSnapshots(null);
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) throw new Error("Not authenticated");
        const r = await fetch(
          `/api/kpi-history?key=${encodeURIComponent(dataKey)}&kpi_id=${encodeURIComponent(kpiId)}&days=30`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) setSnapshots(data.snapshots || []);
      } catch (err) {
        if (!cancelled) setError(String(err.message || err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, kpiId, dataKey]);

  const chartData = useMemo(() => {
    if (!snapshots) return [];
    return snapshots.map(s => ({
      date: s.date.slice(5),  // "MM-DD" for axis label
      fullDate: s.date,
      value: s.value,
    }));
  }, [snapshots]);

  // Compute deltas vs previous row
  const tableRows = useMemo(() => {
    if (!snapshots) return [];
    return snapshots.map((s, i) => {
      const prev = i > 0 ? snapshots[i - 1] : null;
      const delta = prev ? s.value - prev.value : null;
      const deltaPct = prev && prev.value !== 0 ? (delta / Math.abs(prev.value)) * 100 : null;
      return { ...s, delta, deltaPct };
    }).reverse();  // newest first
  }, [snapshots]);

  const label = KPI_LABELS[kpiId] || kpiId;

  return (
    <BottomSheet open={open} onClose={onClose} title={`📊 ${label} — History`} maxWidth={620}>
      <div style={{ padding: "4px 0 16px" }}>
        {loading && (
          <div style={{ textAlign: "center", padding: 40, color: T.textSub }}>Loading…</div>
        )}
        {error && (
          <div style={{ padding: 14, background: T.red + "10", border: `1px solid ${T.red}55`, borderRadius: RADIUS.md, color: T.red, fontSize: 13 }}>
            Failed to load history: {error}
          </div>
        )}
        {!loading && !error && snapshots && snapshots.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: T.textSub, fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.4 }}>📅</div>
            <div style={{ fontWeight: 600, color: T.text, marginBottom: 6 }}>No history yet</div>
            <div>The first snapshot will be taken at midnight UTC tonight.<br/>
              The history grows by one row per day automatically.</div>
          </div>
        )}
        {!loading && !error && snapshots && snapshots.length > 0 && (
          <>
            {/* Sparkline */}
            <div style={{ height: 160, marginBottom: 14 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="kpiHistGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={T.accent} stopOpacity={0.6} />
                      <stop offset="100%" stopColor={T.accent} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: T.textSub, fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: T.textSub, fontSize: 10 }} axisLine={false} tickLine={false} width={50}
                    tickFormatter={v => formatValue(kpiId, v)} />
                  <RechartsTooltip
                    formatter={v => [formatValue(kpiId, v), label]}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.fullDate || label}
                    contentStyle={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12, fontFamily: FONT }}
                  />
                  <Area type="monotone" dataKey="value" stroke={T.accent} strokeWidth={2} fill="url(#kpiHistGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Table */}
            <div style={{ maxHeight: 280, overflowY: "auto", border: `1px solid ${T.border}`, borderRadius: RADIUS.md }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: FONT }}>
                <thead>
                  <tr style={{ background: T.surface, borderBottom: `1px solid ${T.border}` }}>
                    <th style={{ padding: "8px 12px", textAlign: "left", color: T.textSub, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", fontSize: 10 }}>Date</th>
                    <th style={{ padding: "8px 12px", textAlign: "right", color: T.textSub, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", fontSize: 10 }}>Value</th>
                    <th style={{ padding: "8px 12px", textAlign: "right", color: T.textSub, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", fontSize: 10 }}>Δ vs prev</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((r, i) => {
                    const deltaColor = r.delta == null ? T.textSub
                      : r.delta > 0 ? T.green
                      : r.delta < 0 ? T.red
                      : T.textSub;
                    return (
                      <tr key={r.date} style={{ borderBottom: i < tableRows.length - 1 ? `1px solid ${T.border}` : "none" }}>
                        <td style={{ padding: "8px 12px", color: T.text }}>{r.date}</td>
                        <td style={{ padding: "8px 12px", textAlign: "right", color: T.text, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                          {formatValue(kpiId, r.value)}
                        </td>
                        <td style={{ padding: "8px 12px", textAlign: "right", color: deltaColor, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                          {r.delta == null ? "—"
                            : (r.delta > 0 ? "+" : "") + formatValue(kpiId, r.delta)
                              + (r.deltaPct != null ? ` (${r.deltaPct > 0 ? "+" : ""}${r.deltaPct.toFixed(1)}%)` : "")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </BottomSheet>
  );
}
